// Insights (Phase 2 preview): a private index of where your reading and sharing has gone. Descriptive,
// never evaluative: no goals, no scores, no comparisons with anyone, no daily activity chart, no
// reading time, no streaks, no ladders. Works and writers come from the book sections in plan days you
// marked read and the highlights you shared or saved from the library (data/insights.ts); books of the
// Bible from what you shared, saved and reflected on; plans finished from your plans. For the pastor
// or teacher (PRD §3), it also says plainly what your sharing drew.
//
// Works stand as books, each with a dial of the reader's own progress through it (FS-375: never a
// zero, never compared); writers stand as their plates.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { finishedPlans, readLine, useReadJourneys, worksRead, type WorkRead } from "../../data/insights";
import { useJourneys } from "../../data/planQueries";
import { AuthorPlate } from "../../components/AuthorPlate";
import { Dial } from "../../components/Dial";
import { EmptyState } from "../../components/Empty";
import { ShelfBook } from "../../components/library/Shelf";
import { SectionHeader } from "../../components/SectionHeader";
import { toneOf, useAuthors, useCatalogue, type Work } from "../../data/works";
import ui from "../../components/ui.module.css";
import { useFeed, useProfilePosts, useSaved, useSession } from "../../data/queries";
import { REACTIONS, REACTION_LABEL, type Post } from "../../data/types";
import { canonPosition } from "../../lib/canon";
import { useReflections } from "../../data/studyQueries";
import { useTitle } from "../../lib/useTitle";
import s from "./insights.module.css";

export const Route = createFileRoute("/_app/insights")({ component: Insights });

function bookOf(key: string | undefined) {
  return key ? canonPosition(key) : null;
}

function Insights() {
  useTitle("Insights");
  const { data: session } = useSession();
  const handle = session?.user.handle ?? "";
  const { data: mine = [] } = useProfilePosts(handle);
  const { data: saved = [] } = useSaved();
  const { data: feed } = useFeed();
  const { data: reflections = [] } = useReflections();

  const books = useMemo(() => {
    const tally = new Map<string, { name: string; order: number; n: number }>();
    const add = (key: string | undefined) => {
      const b = bookOf(key);
      if (!b) return;
      const t = tally.get(b.book) ?? { name: b.name, order: b.order, n: 0 };
      t.n += 1;
      tally.set(b.book, t);
    };
    mine.forEach((p) => add(p.ref?.key));
    saved.forEach((p) => add(p.ref?.key));
    reflections.forEach((r) => add(r.refKey));
    return [...tally.values()].sort((a, b) => b.n - a.n || a.order - b.order).slice(0, 6);
  }, [mine, saved, reflections]);

  const { data: catalogue } = useCatalogue();
  const { data: authors } = useAuthors();
  const { data: journeys = [] } = useJourneys();
  const { data: readJourneys = [] } = useReadJourneys();
  const read = useMemo(() => worksRead(readJourneys, [...mine, ...saved], catalogue, authors), [readJourneys, mine, saved, catalogue, authors]);
  const finished = finishedPlans(journeys);

  const together = useMemo(() => {
    const myBooks = new Set(mine.map((p) => bookOf(p.ref?.key)?.book).filter(Boolean));
    const out = new Map<string, Set<string>>();
    (feed?.items ?? [])
      .filter((p: Post) => p.author.id !== session?.user.id)
      .forEach((p) => {
        const b = bookOf(p.ref?.key);
        if (b && myBooks.has(b.book)) out.set(b.name, (out.get(b.name) ?? new Set()).add(p.author.displayName.split(" ")[0]!));
      });
    return [...out.entries()];
  }, [mine, feed, session]);

  const drew = useMemo(() => {
    const totals = Object.fromEntries(REACTIONS.map((r) => [r, 0])) as Record<(typeof REACTIONS)[number], number>;
    let comments = 0;
    mine.forEach((p) => {
      REACTIONS.forEach((r) => (totals[r] += p.reactions[r] - (p.myReaction === r ? 1 : 0)));
      comments += p.commentCount;
    });
    return { totals, comments };
  }, [mine]);

  const max = Math.max(1, ...books.map((b) => b.n));

  return (
    <div className={ui.pageWide}>
      <header className={s.head}>
        <h1 className={ui.title}>Insights</h1>
        <p className={ui.subtitle}>Only you see this page. Nothing here is a score, and nothing is compared with anyone.</p>
      </header>

      {/* A bento of panels (FW-006): the works and writers you've read on the left, the quieter indexes beside them. */}
      <div className={s.bento}>
        <div className={s.mainCol}>
          <section className={s.section} aria-labelledby="works-head">
            <SectionHeader id="works-head" title="Works and writers you’ve read" description="From the book sections in plan days you marked read, and the highlights you shared or saved." />
            {read.works.length === 0 ? (
              <EmptyState compact title="Nothing from the library yet." icon="library" body="Read a book alongside a plan, or share a highlight from one.">
                <Link to="/authors" className={ui.secondary}>
                  Find a writer
                </Link>
              </EmptyState>
            ) : (
              <>
                <ul className={s.works}>
                  {read.works.map((w) => (
                    <WorkCard key={w.libraryId} read={w} work={catalogue?.works.find((x) => x.id === w.libraryId)} tone={toneOf(catalogue?.works.find((x) => x.id === w.libraryId), authors)} />
                  ))}
                </ul>
                {read.writers.length > 0 && (
                  <>
                    <h3 className={s.subTitle}>Writers</h3>
                    <ul className={s.writers}>
                      {read.writers.map((x) => (
                        <li key={x.author.id}>
                          <Link to="/authors/$id" params={{ id: x.author.id }} className={s.writer}>
                            <span className={s.writerPlate}>
                              <AuthorPlate name={x.author.name} tone={x.author.tone} size={72} />
                            </span>
                            <span className={s.writerWords}>
                              <span className={s.writerName}>{x.author.name}</span>
                              <span className={s.writerLine}>{readLine(x)}</span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </section>
        </div>
        <div className={s.sideCol}>
          <section className={s.section} aria-labelledby="books-head">
            <SectionHeader id="books-head" title="Books of the Bible you keep returning to" description="Counted from what you shared, saved and reflected on." />
            {books.length === 0 ? (
              <p className={s.quiet}>Share or save a verse and its book will be noted here.</p>
            ) : (
              <table className={s.index}>
                <caption className="visually-hidden">Books you returned to, with the number of times</caption>
                <tbody>
                  {books.map((b) => (
                    <tr key={b.name}>
                      <th scope="row">{b.name}</th>
                      <td className={s.barCell} aria-hidden="true">
                        <span className={s.bar} style={{ inlineSize: `${(b.n / max) * 100}%` }} />
                      </td>
                      <td className={s.n}>
                        {b.n} {b.n === 1 ? "time" : "times"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          {together.length > 0 && (
            <section className={s.section} aria-labelledby="together-head">
              <SectionHeader id="together-head" title="Reading alongside friends" />
              <ul className={s.prose}>
                {together.map(([book, people]) => (
                  <li key={book}>
                    You and {[...people].join(" and ")} have both shared from <strong>{book}</strong>.
                  </li>
                ))}
              </ul>
            </section>
          )}

          {finished.length > 0 && (
            <section className={s.section} aria-labelledby="finished-head">
              <SectionHeader id="finished-head" title="Plans you’ve finished" />
              <ul className={s.prose}>
                {finished.map((j) => (
                  <li key={j.id}>
                    <Link to="/plans/$journeyId" params={{ journeyId: j.id }}>
                      {j.plan.title}
                    </Link>
                    , {j.plan.days} days
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className={s.section} aria-labelledby="drew-head">
            <SectionHeader id="drew-head" title="What your sharing drew" />
            <p className={s.prose}>
              {REACTIONS.filter((r) => drew.totals[r] > 0)
                .map((r) => `${REACTION_LABEL[r]} ${drew.totals[r]}`)
                .join(", ") || "No responses yet"}
              {drew.comments > 0 && `, and ${drew.comments} ${drew.comments === 1 ? "comment" : "comments"}`}
            </p>
            <p className={ui.hint}>Across {mine.length === 1 ? "the post" : `the ${mine.length} posts`} you’ve shared with friends.</p>
          </section>
        </div>
      </div>

      <p className={s.more}>
        See your plans and the map of what you’ve read in <Link to="/plans">Plans</Link>.
      </p>
    </div>
  );
}

/** One work read: the book standing, its title, and a dial of the reader's own way through it. */
function WorkCard({ read, work, tone }: { read: WorkRead; work?: Work; tone?: string }) {
  const of = work ? Math.max(work.pages, read.sections) : 0;
  return (
    <li className={s.work}>
      <ShelfBook id={read.libraryId} title={read.title} shortTitle={work?.shortTitle} author={work?.author} tone={tone} kind={work?.kind} height={112} />
      <div className={s.workWords}>
        <Link to="/books/$id" params={{ id: read.libraryId }} className={s.workName}>
          {read.title}
        </Link>
        {work?.author && <span className={s.writerLine}>{work.author}</span>}
        <span className={s.writerLine}>{readLine(read)}</span>
      </div>
      {read.sections > 0 && of > 0 && <Dial value={read.sections} max={of} label={work && work.pages > 1 ? "Sections read" : "Read"} size={72} />}
    </li>
  );
}
